import { Button, ButtonGroup, Grid, Box } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { bindActionCreators } from "redux";
import AlertDialog from "../../../components/alert-dialog/AlertDialog";
import { HeaderBlock } from "../../../components/header-block/HeaderBlock";
import { Loading } from "../../../components/loading/Loading";
import PageContainer from "../../../components/page-container/PageContainer";
import { PagePlaceholder } from "../../../components/page-placeholder/PagePlaceholder";
import Search from "../../../components/search/Search";
import SimpleTable from "../../../components/table/Table";
import ScrollableTabs from "../../../components/tabs/Tabs";
import { getIcon } from "../../../icon/icon";
import { getTargetGroups } from "../../../state/actions/TargetGroupActions";
import { ModelType, modelTypeToIndex } from "../../../state/constants/ModelType";
import { TargetGroupType } from "../../../state/constants/TargetGroupTypes";
import styles from "./ListTargetGroupsPage.module.scss";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux'
import TableV2 from "components/table/TableV2";
import { useTranslation } from 'react-i18next'
import { FixMeLater } from "types/FixMeLaterType";
import { RootState } from 'state/reducers/AppReducer'

interface Cell {
  id?: string,
  clickable?: FixMeLater,
  action?: FixMeLater,
  align?: string,
  handleClick?: ((event: FixMeLater, row: FixMeLater) => void)
}

interface TargetGroup extends FixMeLater { }

function ListTargetGroups(props: FixMeLater) {
  // Selectors
  const targetGroups = useSelector<RootState, TargetGroup>(state => state.TargetGroupReducer.targetGroups)

  // Page Utility
  const dispatch = useDispatch()
  const [t, i18n] = useTranslation()

  // Page content states.
  const [currentModelType, setCurrentModelType] = useState(props.type ?? ModelType.CUSTOMERS);
  const [fetchTargetGroups, setFetchTargetGroups] = useState(true);

  // Search-related states.
  const [originalData, setOriginalData] = useState<FixMeLater[]>([]);
  const [searchedData, setSearchedData] = useState<FixMeLater[]>([]);

  // Page Meta states.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setFetchTargetGroups(true);
  }, [currentModelType])

  useEffect(() => {
    if (fetchTargetGroups) {
      // State mgmt
      setFetchTargetGroups(false);
      setLoading(true);

      // Dispatch API Call
      dispatch(getTargetGroups(currentModelType, (success: boolean) => {
        setLoading(false)
      }))
    }
  }, [fetchTargetGroups])

  useEffect(() => {
    const data = createData(targetGroups);
    setOriginalData(data);
    setSearchedData(data);
  }, [targetGroups])

  const renderData = () => {
    const headers = createHeaders();
    const cells = createCells(headers);

    const data = searchedData != null && searchedData.length === 0 ?
      <PagePlaceholder
        image={getIcon("frame-expand.svg")}
        text={t('target-group.none-found')}
      /> :
      <SimpleTable
        isSelectable={false}
        dataId={"targetGroupId"}
        cells={cells}
        headers={headers}
        rows={searchedData}
      />

    return <ScrollableTabs
      handleTabChange={(newValue: number) => onTabChange(newValue)}
      value={props.type === TargetGroupType.DISTRIBUTOR ? 0 : modelTypeToIndex(currentModelType)}
      tabs={

        props.type === TargetGroupType.DISTRIBUTOR ?
          [
            {
              name: t('distributor.distributors'),
              content: loading ? <Loading /> : data
            }
          ] :
          [
            {
              name: t('customer.customers'),
              content: loading ? <Loading /> : data
            },
            {
              name: t('product.products'),
              content: loading ? <Loading /> : data
            },
            {
              name: t('distributor.distributors'),
              content: loading ? <Loading /> : data
            }
          ]
      }
    />
  }

  const createHeaders = () => {
    return [
      {
        id: "name",
        clickable: true,
        action: {
          path: props.type === TargetGroupType.DISTRIBUTOR ? "/distributors/{id}" : "/target-groups/{id}",
          identifier: "targetGroupId"
        },
        align: "left",
        label: props.match.url ==="/distributors" ? t('common.name') : t('target-group.name')
      },
      {
        id: "targetGroupId",
        clickable: false,
        align: "left",
        label: props.match.url ==="/distributors" ? t('distributor.id') : t('target-group.id')
      }
    ];
  }

  const createCells = (headers: FixMeLater[]) => {
    let cells: FixMeLater[] = [];

    headers.forEach(header => {
      let cell: Cell = {};

      cell.id = header.id;

      if (header.hasOwnProperty("action")) {
        cell.handleClick = (event: FixMeLater, row: FixMeLater) => {
          let path = header.action.path.replace(
            "{id}",
            row[header.action.identifier]
          );
          props.history.push(path);
        };
      }

      cell.align = header.align;
      cell.clickable = header.clickable;

      cells.push(cell);
    });

    return cells;
  }

  const createData = (targetGroups: FixMeLater) => {
    const currentTargetGroups = targetGroups[currentModelType] || {}
    const targetGroupKeys = Object.keys(currentTargetGroups);

    return targetGroupKeys.map(targetGroupKey => {
      const targetGroup = currentTargetGroups[targetGroupKey];

      return {
        targetGroupId: targetGroupKey,
        name: targetGroup.DisplayName
      };
    });
  }


  const onTabChange = (newValue: number) => {
    const modelType = newValue === modelTypeToIndex(ModelType.CUSTOMERS) ?
      ModelType.CUSTOMERS :
      newValue === modelTypeToIndex(ModelType.PRODUCTS) ?
        ModelType.PRODUCTS : ModelType.DISTRIBUTOR
    setCurrentModelType(modelType);
  }

  return (
    <Box>
      <HeaderBlock
        title={props.type === TargetGroupType.DISTRIBUTOR ? t('target-group.distributor-target-groups') : t('target-group.target-groups')}
        right={
          <Grid container spacing={2} alignItems="center" justify="flex-end">
            <Grid item>
              <Search
                searchField={"name"}
                secondaryFields={["targetGroupId"]}
                returnSearchMatches={(data: FixMeLater) => {
                  setSearchedData(data)
                }}
                data={originalData}
                searchTitle={props.match.url ==="/distributors" ? t('distributor.search') : t('target-group.search')}
              />
            </Grid>
            {props.match.url =="/target-groups"  &&  (
              <Grid item>
              <ButtonGroup classes={{ root: styles.buttonGroup }}>
                <Button
                  onClick={() => { props.history.push(props.type === TargetGroupType.DISTRIBUTOR ? "distributors/new" : "target-groups/new"); }}
                  variant="outlined"
                  color="secondary"
                >
                  {t('target-group.new')}
                </Button>
              </ButtonGroup>
            </Grid>)
            }
          </Grid>
        }
      />
      <PageContainer hasFooter>
        {renderData()}
      </PageContainer>
    </Box>
  )
};

export default ListTargetGroups

